/**
 * @growhats/webphone
 *
 * Standalone webphone widget for WhatsApp VoIP calls.
 * API-compatible with @wavoip/wavoip-webphone for drop-in replacement.
 *
 * Usage (same as Wavoip):
 *   const api = await GrowhatsWebphone.render({
 *     serverUrl: 'https://api.gapiv2.com',
 *     accountId: 1,
 *     inboxId: 81,
 *     apiKey: 'your-api-key',
 *   });
 *
 *   // Make a call (same as Wavoip)
 *   const call = await api.call.start('5511999999999');
 *
 *   // Listen for incoming calls (same as Wavoip)
 *   api.call.onOffer((offer) => {
 *     offer.accept(); // or offer.reject()
 *   });
 *
 *   // Device management (maps inbox → device)
 *   const devices = api.device.getDevices();
 */

import { GrowhatsWebphone as WebphoneCore, resolveSocketUrl } from './core';
export { resolveSocketUrl };
import { renderWidget } from './ui/widget';
import type { WebphoneConfig, WebphoneAPI } from './types';

export type {
  WebphoneConfig, WebphoneAPI,
  CallOffer, CallActive, CallOutgoing, CallStatus, CallDirection, CallStats,
  Device, DeviceStatus,
  Notification, NotificationType,
  WebphoneEvents,
  PositionPreset,
  RecordingConfig, RecordingAPI, RecordingResult,
} from './types';

export class GrowhatsWebphone {
  private static instance: WebphoneCore | null = null;

  /**
   * Render the webphone widget and connect to the server.
   * Returns a Promise resolving to the WebphoneAPI (Wavoip-compatible).
   */
  static async render(config: WebphoneConfig): Promise<WebphoneAPI> {
    if (GrowhatsWebphone.instance) {
      GrowhatsWebphone.instance.destroy();
    }

    const core = new WebphoneCore(config);
    GrowhatsWebphone.instance = core;

    // Render widget UI first (always shows, even if server is offline)
    const container = config.container || document.body;
    const cleanup = renderWidget(core, container);
    core._onDestroy = cleanup;

    // Expose the api globally (compatible with both Wavoip and Growhats).
    // Note: we intentionally overwrite the class-level shortcut on lowercase
    // aliases here — once the user has rendered, they want the api instance.
    const api = core.getAPI();
    (window as any).growhatsWebphoneAPI = api;       // namespace-safe alias for the api
    (window as any).wavoip = api;                    // Wavoip drop-in compatibility

    // Connect to server (non-blocking)
    core.connect().catch((err: Error) => {
      console.warn('[Webphone] Server connection failed:', err.message);
    });

    return api;
  }

  /**
   * Alias for render() — Wavoip legacy compatibility.
   * Allows: window.wavoipWebphone.render() pattern
   */
  static get wavoipWebphone() {
    return { render: GrowhatsWebphone.render, destroy: GrowhatsWebphone.destroy };
  }

  /**
   * Remove the webphone widget from the DOM and disconnect.
   */
  static destroy(): void {
    if (GrowhatsWebphone.instance) {
      GrowhatsWebphone.instance.destroy();
      GrowhatsWebphone.instance = null;
      delete (window as any).growhatsWebphoneAPI;
      delete (window as any).wavoip;
    }
  }
}

// Expose the class globally under several names so the customer can call
// `render()` regardless of which convention they learned (Wavoip vs Growhats,
// camelCase vs PascalCase).
//
// The UMD bundler (Vite with `exports: 'named'`) exposes
// `window.GrowhatsWebphone` as a *namespace* object containing every named
// export — i.e. `window.GrowhatsWebphone.GrowhatsWebphone` is the class. That
// breaks `GrowhatsWebphone.render()` for anyone who doesn't know the unwrap
// idiom. We overwrite it here, preserving the type exports as properties on
// the class so `GrowhatsWebphone.CallActive` etc still resolve.
if (typeof window !== 'undefined') {
  const w = window as any;
  const existing = w.GrowhatsWebphone;
  if (existing && typeof existing === 'object' && existing.GrowhatsWebphone === GrowhatsWebphone) {
    // UMD namespace wrap — copy its other keys onto the class and overwrite
    for (const key of Object.keys(existing)) {
      if (key !== 'GrowhatsWebphone' && !(key in GrowhatsWebphone)) {
        (GrowhatsWebphone as any)[key] = existing[key];
      }
    }
  }
  w.GrowhatsWebphone = GrowhatsWebphone;
  w.growhatsWebphone = GrowhatsWebphone;   // legacy camelCase alias
  w.wavoipWebphone  = GrowhatsWebphone;    // Wavoip drop-in alias
}
